/**
 * <s-sponsor-form> — Wraps the sponsorship application <form>. On submit it folds the
 * applicant's social profile URLs and requested code into the customer form's
 * contact[tags] field so each application lands on a customer record the admin can review.
 *
 * Required child markup:
 *   form                         — the customer form.
 *   input[data-tags-base]        — hidden contact[tags] input; its initial value is the base tag list.
 *   input[data-social]           — profile URL inputs; the attribute value is the platform key.
 *   input[data-code]             — requested code input.
 *   [data-social-error]          — message shown when no profile is entered.
 *
 * Values from this form are applicant-supplied and untrusted. Approval state is never
 * written from the storefront.
 */
class SponsorForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.tagsInput = this.querySelector('[data-tags-base]');
    if (!this.form || !this.tagsInput) return;
    this.baseTags = this.tagsInput.value;
    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
    this.codeInput = this.querySelector('[data-code]');
    if (this.codeInput) {
      this.onCodeInput = () => {
        this.codeInput.value = this.codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      };
      this.codeInput.addEventListener('input', this.onCodeInput);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.codeInput?.removeEventListener('input', this.onCodeInput);
  }

  onSubmit(event) {
    const socials = [...this.querySelectorAll('[data-social]')];
    const filled = socials.filter((input) => input.value.trim() !== '');
    const error = this.querySelector('[data-social-error]');

    if (filled.length === 0) {
      event.preventDefault();
      if (error) error.hidden = false;
      socials[0]?.setAttribute('aria-invalid', 'true');
      socials[0]?.focus();
      return;
    }
    if (error) error.hidden = true;
    socials.forEach((input) => input.removeAttribute('aria-invalid'));

    const tags = [this.baseTags];
    filled.forEach((input) => {
      const url = input.value.trim().replace(/,/g, '%2C').slice(0, 200);
      tags.push(`sponsor-${input.dataset.social}:${url}`);
    });
    const code = this.codeInput?.value.trim();
    if (code) tags.push(`sponsor-code-request:${code}`);

    this.tagsInput.value = tags.join(', ');
  }
}

if (!customElements.get('s-sponsor-form')) {
  customElements.define('s-sponsor-form', SponsorForm);
}
